import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';

/**
 * Hosted style from OpenFreeMap (tiles, glyphs, sprites included).
 * @see https://openfreemap.org/quick_start/
 */
const OPENFREEMAP_STYLE_URL = 'https://tiles.openfreemap.org/styles/positron';

/** Kölner Dom — map view is fixed here; venue marker uses data-lat / data-lng. */
const COLOGNE_DOM_LNG = 6.9583;
const COLOGNE_DOM_LAT = 50.9413;
const DEFAULT_ZOOM = 12;

function documentLangPrefersGerman() {
  const lang = (document.documentElement.getAttribute('lang') || '').toLowerCase();
  return lang.startsWith('de');
}

/**
 * Positron uses coalesce(name_en, name). Prefer name_de when the page is German.
 * @see https://maplibre.org/maplibre-gl-js/docs/examples/change-a-maps-language/
 */
function mapExprPreferNameDe(expr) {
  if (!Array.isArray(expr)) {
    return expr;
  }
  if (expr.length === 2 && expr[0] === 'get' && expr[1] === 'name_en') {
    return ['get', 'name_de'];
  }
  return expr.map(mapExprPreferNameDe);
}

function applyOpenFreeMapGermanLabels(map) {
  const layers = map.getStyle()?.layers ?? [];
  for (const layer of layers) {
    if (layer.type !== 'symbol' || !layer.id) {
      continue;
    }
    const tf = layer.layout?.['text-field'];
    if (!tf) {
      continue;
    }
    const serialized = JSON.stringify(tf);
    if (!serialized.includes('"name_en"')) {
      continue;
    }
    try {
      map.setLayoutProperty(layer.id, 'text-field', mapExprPreferNameDe(JSON.parse(serialized)));
    } catch {
      /* layer may be unavailable in current style state */
    }
  }
}

function finishOpenFreeMapSetup(map) {
  map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');
  map.once('load', () => {
    if (documentLangPrefersGerman()) {
      applyOpenFreeMapGermanLabels(map);
    }
  });
}

function loadEventLocationMap(el) {
  const lat = parseFloat(el.dataset.lat);
  const lng = parseFloat(el.dataset.lng);

  if (Number.isNaN(lat) || Number.isNaN(lng)) {
    el.classList.add('event-location-map--fallback');
    return;
  }

  const map = new maplibregl.Map({
    container: el,
    style: OPENFREEMAP_STYLE_URL,
    center: [COLOGNE_DOM_LNG, COLOGNE_DOM_LAT],
    zoom: DEFAULT_ZOOM,
    attributionControl: true,
  });

  finishOpenFreeMapSetup(map);

  const markerEl = document.createElement('div');
  markerEl.className = 'event-location-map-marker';
  new maplibregl.Marker({ element: markerEl, anchor: 'center' })
    .setLngLat([lng, lat])
    .addTo(map);
}

export function initEventLocationMap() {
  const el = document.querySelector('.js-event-location-map');
  if (el) {
    loadEventLocationMap(el);
  }
}

/**
 * Locations overview: same framing as the event venue map (Kölner Dom, default zoom),
 * Positron style, and the same marker dots. Expects `window.locations_array` from
 * `partials/locations-all.php` (lat + long per item).
 */
export function initLocationsOverviewMap() {
  const container = document.getElementById('locations-map-container');
  const locations = typeof window.locations_array !== 'undefined' ? window.locations_array : null;

  if (!container || !Array.isArray(locations) || locations.length === 0) {
    return;
  }

  const points = locations
    .map((loc) => {
      const lat = parseFloat(loc.lat);
      const lng = parseFloat(loc.long);
      return Number.isNaN(lat) || Number.isNaN(lng) ? null : { lat, lng, title: loc.title, link: loc.link };
    })
    .filter(Boolean);

  if (points.length === 0) {
    return;
  }

  const map = new maplibregl.Map({
    container,
    style: OPENFREEMAP_STYLE_URL,
    center: [COLOGNE_DOM_LNG, COLOGNE_DOM_LAT],
    zoom: DEFAULT_ZOOM,
    attributionControl: true,
  });

  finishOpenFreeMapSetup(map);

  for (const p of points) {
    const markerEl = document.createElement('div');
    markerEl.className = 'event-location-map-marker';
    const marker = new maplibregl.Marker({ element: markerEl, anchor: 'center' }).setLngLat([p.lng, p.lat]).addTo(map);

    if (p.title) {
      const body = document.createElement('div');
      body.className = 'locations-overview-popup';
      if (p.link) {
        const a = document.createElement('a');
        a.href = p.link;
        a.textContent = p.title;
        body.appendChild(a);
      } else {
        body.textContent = p.title;
      }
      marker.setPopup(new maplibregl.Popup({ offset: 12 }).setDOMContent(body));
    }
  }
}
